import { Link } from "react-router-dom";
import { ArrowRight, Gauge, Palette, Fuel, Cog } from "lucide-react";
import { Badge, StatusBadge } from "@/components/ui";
import { cn, formatPrice, formatMileage } from "@/utils/format";
import type { VehicleSummary } from "@/types";

export interface VehicleCardProps {
  vehicle: VehicleSummary;
  showStatus?: boolean;
  className?: string;
}

/**
 * Vehicle card for displaying vehicle summary in grid/list views
 */
export function VehicleCard({
  vehicle,
  showStatus = false,
  className,
}: VehicleCardProps) {
  const title = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const subtitle = vehicle.trim || "";

  return (
    <Link
      to={`/vehicles/${vehicle.id}`}
      className={cn(
        "group relative bg-white rounded-2xl overflow-hidden border border-line-gray hover:border-navy-900/20 transition-all duration-300 hover:shadow-xl",
        className,
      )}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
        {vehicle.photo ? (
          <img
            src={vehicle.photo}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <svg
              className="h-16 w-16 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        {/* Badges overlay */}
        <div className="absolute left-4 top-4 flex flex-wrap gap-2">
          {vehicle.certified && (
            <Badge variant="secondary" className="bg-white/90 text-navy-900 backdrop-blur-sm">
              Certified
            </Badge>
          )}
          {showStatus && vehicle.status !== "available" && (
            <StatusBadge status={vehicle.status} size="sm" />
          )}
        </div>

        {/* Subtle overlay to match Brands cards */}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/20 to-transparent" />
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-headline text-2xl font-light text-navy-900 group-hover:text-primary-600 transition-colors line-clamp-2">
          {title}
        </h3>
        {subtitle && (
          <p className="mt-1 text-sm font-medium tracking-wide text-muted-navy uppercase line-clamp-1">
            {subtitle}
          </p>
        )}

        <p className="mt-4 text-2xl font-semibold text-navy-900">
          {formatPrice(vehicle.priceCents)}
        </p>

        {/* Quick specs */}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-navy">
          <span className="flex items-center gap-1">
            <Gauge className="h-4 w-4" />
            {formatMileage(vehicle.mileage)}
          </span>
          {vehicle.exteriorColor && (
            <span className="flex items-center gap-1">
              <Palette className="h-4 w-4" />
              {vehicle.exteriorColor}
            </span>
          )}
        </div>

        {/* Tags */}
        <div className="mt-5 flex flex-wrap gap-2">
          {vehicle.fuelType && (
            <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy capitalize inline-flex items-center gap-1">
              <Fuel className="h-3 w-3" />
              {vehicle.fuelType.replace("_", " ")}
            </span>
          )}
          {vehicle.transmission && (
            <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy capitalize inline-flex items-center gap-1">
              <Cog className="h-3 w-3" />
              {vehicle.transmission}
            </span>
          )}
          {vehicle.drivetrain && (
            <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy uppercase">
              {vehicle.drivetrain}
            </span>
          )}
        </div>

        <div className="mt-6 flex items-center text-sm font-medium text-navy-900 group-hover:text-primary-600 transition-colors">
          <span>Explore</span>
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
